import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

const LEVELS = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'special', label: 'Special' },
];

const EMPTY = { name: '', slug: '', level: 'state', jurisdiction_id: '', seat: '' };

export default function OfficesPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/admin/jurisdictions?page_size=500');
        setJurisdictions(res.data.items || []);
      } catch (_e) {}
    })();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page_size: '500' });
      if (levelFilter) params.set('level', levelFilter);
      if (search) params.set('q', search);
      const res = await adminApi.get(`/admin/offices?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [levelFilter, search]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  }

  function openEdit(o) {
    setEditing(o);
    setForm({ name: o.name || '', slug: o.slug || '', level: o.level || 'state', jurisdiction_id: o.jurisdiction_id || '', seat: o.seat || '' });
    setShowForm(true);
  }

  async function save() {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        level: form.level,
        jurisdiction_id: form.jurisdiction_id || null,
        seat: form.seat?.trim() || null,
      };
      if (editing) await adminApi.put(`/admin/offices/${editing.id}`, payload);
      else await adminApi.post('/admin/offices', payload);
      toast.success(editing ? 'Office updated' : 'Office created');
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove(o) {
    if (!window.confirm(`Delete office "${o.name}"?`)) return;
    try {
      await adminApi.delete(`/admin/offices/${o.id}`);
      toast.success('Office deleted');
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  return (
    <>
      <PageHeader
        title="Offices"
        subtitle="Role catalog used by Races (e.g. Governor of Florida, US House FL-27, Miami-Dade Mayor)."
        actions={<Button variant="primary" onClick={openCreate} data-testid="offices-new-button"><i className="bi bi-plus-lg me-1" /> New office</Button>}
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex gap-2 flex-wrap">
              <div style={{ minWidth: 240 }}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Search</Form.Label>
                <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. governor" data-testid="offices-search-input" />
              </div>
              <div style={{ minWidth: 200 }}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Level</Form.Label>
                <Form.Select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} data-testid="offices-level-filter">
                  <option value="">All levels</option>
                  {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Form.Select>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state"><i className="bi bi-bank" /><div>No offices match.</div></div>
          ) : (
            <Table size="sm" responsive hover className="mb-0">
              <thead><tr><th>Name</th><th>Level</th><th>Slug</th><th>Seat</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} data-testid={`office-row-${o.id}`}>
                    <td className="fw-semibold">{o.name}</td>
                    <td><Badge bg="secondary">{o.level}</Badge></td>
                    <td><code>{o.slug}</code></td>
                    <td>{o.seat || ''}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(o)} data-testid={`office-edit-${o.id}`}><i className="bi bi-pencil" /></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => remove(o)} data-testid={`office-delete-${o.id}`}><i className="bi bi-trash" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Modal show={showForm} onHide={() => setShowForm(false)} centered>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit office' : 'New office'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="office-name-input" /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Slug</Form.Label><Form.Control value={form.slug} placeholder="auto-generated" onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} data-testid="office-slug-input" /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Level</Form.Label>
              <Form.Select value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))} data-testid="office-level-select">
                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>Jurisdiction (optional)</Form.Label>
              <Form.Select value={form.jurisdiction_id} onChange={(e) => setForm((f) => ({ ...f, jurisdiction_id: e.target.value }))} data-testid="office-jurisdiction-select">
                <option value="">— None —</option>
                {jurisdictions.map((j) => <option key={j.id} value={j.id}>{j.type}: {j.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2"><Form.Label>Seat (optional)</Form.Label><Form.Control value={form.seat} onChange={(e) => setForm((f) => ({ ...f, seat: e.target.value }))} placeholder="e.g. Seat 2, At-Large" data-testid="office-seat-input" /></Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving} data-testid="office-save-button">{saving ? <Spinner size="sm" animation="border" /> : 'Save'}</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}
